import React, { useState, useEffect } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { apiService } from '../../services/api';
import { CreateVisitRequest } from '../../types/visit';
import { formatUTCDate, formatDateForBackend, parseDateStringToLocal } from '../../utils/dateHelpers';

interface NewVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prefilledProvider?: {
    npi: string;
    name: string;
    specialty?: string;
    address?: string;
  };
}

export const NewVisitModal: React.FC<NewVisitModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefilledProvider
}) => {
  const [loading, setLoading] = useState(false);
  const [lookingUpNPI, setLookingUpNPI] = useState(false);
  const [error, setError] = useState('');
  const [npiNotFoundError, setNpiNotFoundError] = useState('');
  const [npiInput, setNpiInput] = useState('');
  const [unhandledFollowUps, setUnhandledFollowUps] = useState<any[]>([]);
  const [selectedParentVisitId, setSelectedParentVisitId] = useState<string>('');
  const [formData, setFormData] = useState<CreateVisitRequest>({
    provider: {
      npi: '',
      name: '',
      specialty: '',
      address: ''
    },
    visitDate: formatDateForBackend(new Date()),
    visitType: 'initial',
    status: 'scheduled',
    purpose: '',
    outcome: '',
    nextVisitRequired: false,
    nextVisitDate: '',
    tags: []
  });

  useEffect(() => {
    if (prefilledProvider) {
      setFormData(prev => ({
        ...prev,
        provider: prefilledProvider
      }));
      setNpiInput(prefilledProvider.npi);
    }
  }, [prefilledProvider]);

  useEffect(() => {
    if (isOpen) {
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchUnhandledFollowUps = async () => {
      if (formData.visitType === 'follow-up' && formData.provider.npi) {
        try {
          const response = await apiService.getUnhandledFollowUps(formData.provider.npi);
          setUnhandledFollowUps(response.visits || []);
          
          // If no unhandled follow-ups, automatically set visit type to initial
          if (!response.visits || response.visits.length === 0) {
            setFormData(prev => ({ ...prev, visitType: 'initial' }));
            setError('No unhandled follow-up visits found for this provider. Visit type changed to Initial.');
          }
        } catch (err) {
          console.error('Failed to fetch unhandled follow-ups:', err);
          setUnhandledFollowUps([]);
        }
      } else {
        setUnhandledFollowUps([]);
        setSelectedParentVisitId('');
      }
    };

    fetchUnhandledFollowUps();
  }, [formData.visitType, formData.provider.npi]);

  const handleNPILookup = async () => {
    if (!npiInput || npiInput.length < 10) {
      return;
    }

    setLookingUpNPI(true);
    setError('');
    setNpiNotFoundError('');

    try {
      const provider = await apiService.getProviderByNPI(npiInput);
      
      if (provider) {
        // Auto-populate provider details
        setFormData(prev => ({
          ...prev,
          provider: {
            npi: provider.npi,
            name: provider.name || '',
            specialty: provider.type || '',
            address: provider.address ? 
              `${provider.address.street1}${provider.address.street2 ? ' ' + provider.address.street2 : ''}, ${provider.address.city}, ${provider.address.state} ${provider.address.zip}` 
              : ''
          }
        }));
        setNpiNotFoundError('');
      } else {
        // NPI not found, reset fields and show error
        setFormData(prev => ({
          ...prev,
          provider: {
            npi: npiInput,
            name: '',
            specialty: '',
            address: ''
          }
        }));
        setNpiNotFoundError(`Provider with NPI ${npiInput} not found. Please enter provider details manually.`);
      }
    } catch (err) {
      console.error('Error looking up NPI:', err);
      // On error, just set the NPI and allow manual entry
      setFormData(prev => ({
        ...prev,
        provider: {
          ...prev.provider,
          npi: npiInput
        }
      }));
    } finally {
      setLookingUpNPI(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare visit data, excluding nextVisitDate if nextVisitRequired is false or if it's empty
      const visitData = { ...formData };
      if (!visitData.nextVisitRequired || !visitData.nextVisitDate) {
        delete visitData.nextVisitDate;
      }

      if (formData.visitType === 'follow-up' && selectedParentVisitId) {
        // Create follow-up visit with parent reference
        await apiService.createFollowUpVisit({
          ...visitData,
          parentVisitId: selectedParentVisitId
        });
      } else {
        // Create regular visit
        await apiService.createVisit(visitData);
      }
      
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        provider: {
          npi: '',
          name: '',
          specialty: '',
          address: ''
        },
        visitDate: formatDateForBackend(new Date()),
        visitType: 'initial',
        status: 'scheduled',
        purpose: '',
        outcome: '',
        nextVisitRequired: false,
        nextVisitDate: '',
        tags: []
      });
      setSelectedParentVisitId('');
      setUnhandledFollowUps([]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create visit');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: Date | null, field: 'visitDate' | 'nextVisitDate') => {
    if (date) {
      // Store as yyyy-MM-dd format for sending to backend
      // Backend will convert to UTC
      setFormData(prev => ({
        ...prev,
        [field]: formatDateForBackend(date)
      }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule New Visit</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Provider Information */}
          <div className="space-y-4">
            <h3 className="font-semibold">Provider Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="npi">Provider NPI *</Label>
                <div className="flex gap-2">
                  <Input
                    id="npi"
                    value={npiInput}
                    onChange={(e) => {
                      setNpiInput(e.target.value);
                      setNpiNotFoundError(''); // Clear error when NPI is changed
                      // Clear provider data when NPI is changed
                      if (!prefilledProvider) {
                        setFormData(prev => ({
                          ...prev,
                          provider: { ...prev.provider, npi: e.target.value }
                        }));
                      }
                    }}
                    onBlur={handleNPILookup}
                    placeholder="Enter 10-digit NPI"
                    required
                    disabled={!!prefilledProvider}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleNPILookup}
                    disabled={!!prefilledProvider || lookingUpNPI}
                  >
                    {lookingUpNPI ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {lookingUpNPI && (
                  <p className="text-sm text-muted-foreground mt-1">Looking up provider details...</p>
                )}
                {npiNotFoundError && (
                  <p className="text-sm text-destructive mt-1">{npiNotFoundError}</p>
                )}
              </div>
              <div>
                <Label htmlFor="name">Provider Name *</Label>
                <Input
                  id="name"
                  value={formData.provider.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    provider: { ...prev.provider, name: e.target.value }
                  }))}
                  required
                  disabled={!!prefilledProvider || lookingUpNPI}
                />
              </div>
              <div>
                <Label htmlFor="specialty">Specialty</Label>
                <Input
                  id="specialty"
                  value={formData.provider.specialty || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    provider: { ...prev.provider, specialty: e.target.value }
                  }))}
                  disabled={!!prefilledProvider || lookingUpNPI}
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.provider.address || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    provider: { ...prev.provider, address: e.target.value }
                  }))}
                  disabled={!!prefilledProvider || lookingUpNPI}
                />
              </div>
            </div>
          </div>

          {/* Visit Details */}
          <div className="space-y-4">
            <h3 className="font-semibold">Visit Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="visitDate">Visit Date *</Label>
                <DatePicker
                  selected={parseDateStringToLocal(formData.visitDate) || new Date()}
                  onChange={(date: Date | null) => handleDateChange(date, 'visitDate')}
                  dateFormat="MMM d, yyyy"
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <Label htmlFor="visitType">Visit Type *</Label>
                <Select
                  value={formData.visitType}
                  onValueChange={(value) => setFormData(prev => ({
                    ...prev,
                    visitType: value as 'initial' | 'follow-up'
                  }))}
                  disabled={formData.visitType !== 'follow-up'}
                >
                  <SelectTrigger disabled={formData.visitType !== 'follow-up'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial">Initial Visit</SelectItem>
                    {/* <SelectItem value="follow-up">Follow-up</SelectItem> */}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({
                    ...prev,
                    status: value as 'scheduled' | 'completed' | 'cancelled' | 'no-show'
                  }))}
                  disabled
                >
                  <SelectTrigger disabled>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no-show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Follow-up Parent Visit Selection */}
            {formData.visitType === 'follow-up' && unhandledFollowUps.length > 0 && (
              <div>
                <Label htmlFor="parentVisit">Select Parent Visit *</Label>
                <Select
                  value={selectedParentVisitId}
                  onValueChange={(value) => {
                    setSelectedParentVisitId(value);
                    // Pre-fill purpose and date from parent visit
                    const parentVisit = unhandledFollowUps.find(v => v.id === value);
                    if (parentVisit) {
                      setFormData(prev => ({
                        ...prev,
                        purpose: `Follow-up for visit on ${formatUTCDate(parentVisit.visitDate)}`,
                        visitDate: parentVisit.nextVisitDate ? 
                          format(new Date(parentVisit.nextVisitDate), 'yyyy-MM-dd') : 
                          prev.visitDate
                      }));
                    }
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a visit that requires follow-up" />
                  </SelectTrigger>
                  <SelectContent>
                    {unhandledFollowUps.map((visit) => (
                      <SelectItem key={visit.id} value={visit.id}>
                        {formatUTCDate(visit.visitDate)} - {visit.visitedBy?.name || 'Unknown'}
                        {visit.outcome && ` - ${visit.outcome.substring(0, 50)}...`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="purpose">Purpose of Visit</Label>
              <Textarea
                id="purpose"
                value={formData.purpose || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                rows={3}
                placeholder="Describe the purpose of this visit..."
              />
            </div>

            {formData.status === 'completed' && (
              <div>
                <Label htmlFor="outcome">Visit Outcome</Label>
                <Textarea
                  id="outcome"
                  value={formData.outcome || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, outcome: e.target.value }))}
                  rows={3}
                  placeholder="Describe the outcome of this visit..."
                />
              </div>
            )}
          </div>

          {/* Follow-up Information */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="nextVisitRequired"
                checked={formData.nextVisitRequired || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  nextVisitRequired: e.target.checked
                }))}
                className="rounded border-gray-300"
              />
              <Label htmlFor="nextVisitRequired" className="cursor-pointer">
                Follow-up visit required
              </Label>
            </div>

            {formData.nextVisitRequired && (
              <div>
                <Label htmlFor="nextVisitDate">Next Visit Date</Label>
                <DatePicker
                  selected={formData.nextVisitDate ? parseDateStringToLocal(formData.nextVisitDate) : null}
                  onChange={(date: Date | null) => handleDateChange(date, 'nextVisitDate')}
                  dateFormat="MMM d, yyyy"
                  className="w-full px-3 py-2 border rounded-md"
                  minDate={new Date()}
                  placeholderText="Select next visit date"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Visit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
